import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Create client with improved configuration
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storage: {
      getItem: (key) => {
        try {
          const value = localStorage.getItem(key);
          if (!value) return null;
          // Validate session data
          if (key.includes('auth.token')) {
            const session = JSON.parse(value);
            if (new Date(session.expires_at * 1000) < new Date()) {
              localStorage.removeItem(key);
              return null;
            }
          }
          return value;
        } catch {
          return null;
        }
      },
      setItem: (key, value) => {
        try {
          localStorage.setItem(key, value);
          return Promise.resolve();
        } catch {
          return Promise.resolve();
        }
      },
      removeItem: (key) => {
        try {
          localStorage.removeItem(key);
          return Promise.resolve();
        } catch {
          return Promise.resolve();
        }
      }
    }
  },
  global: {
    headers: {
      'x-client-info': `vite-react@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,
    },
  },
  db: {
    schema: 'public'
  },
  // Add retry configuration
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// NOTE: Auth state change listener moved to AuthContext to prevent duplicate listeners
// The global listener here was causing memory leaks and duplicate state updates

// Export a function to check connection status
export const checkConnection = async () => {
  try {
    const { data, error } = await supabase.from('profiles').select('id').limit(1);
    return !error;
  } catch {
    return false;
  }
};

// Export a function to retry failed requests
export const retryRequest = async <T>(
  request: () => Promise<T>,
  maxRetries = 3,
  delay = 1000
): Promise<T> => {
  let lastError: any;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await request();
    } catch (error) {
      lastError = error;
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }
  
  throw lastError;
};

// Initialize realtime subscriptions
let initialized = false;

export const initializeRealtimeSubscriptions = () => {
  if (initialized) return;

  try {
    const channel = supabase.channel('db-changes');

    channel
      .on(
        'postgres_changes',
        { event: '*', schema: 'public' },
        (payload) => {
          console.log('Database change:', payload);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to database changes');
        } else if (status === 'CLOSED') {
          console.log('Lost connection to database changes');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('Error subscribing to database changes');
        }
      });

    initialized = true;
  } catch (error) {
    console.error('Failed to initialize realtime subscriptions:', error);
  }
};